<template>
  <div
    :id="`${source}_custom_color_picker`"
    class="relative inline-block proxima_nova"
    ref="colorPickerRef"
  >
          <div
        class="flex flex-row items-center gap-2 rounded-lg cursor-pointer picker_container"
        :class="{ 'gap-2': variant === variants.VARIANT1 }"
        @click="togglePicker"
      >
        <div
          ref="triggerRef"
          :class="[`size-[${pickerSize}px] rounded flex-shrink-0`]"
          :style="{ backgroundColor: modelValue || '#164746' }"
        ></div>

      <input
        v-if="variant === variants.VARIANT1 && canType"
        v-model="hexInput"
        class="text-sm font-normal primary_color bg-transparent border-none outline-none flex-1"
        @input="handleHexInput"
        @blur="handleHexBlur"
      />
      <span
        v-if="variant === variants.VARIANT1 && !canType"
        class="text-sm font-normal primary_color"
      >
        {{ modelValue || "#164746" }}
      </span>
    </div>

    <div
      v-if="isOpen"
      :class="[
        'z-50 mb-2',
        props.relativeToViewport ? 'fixed' : 'absolute bottom-full left-0'
      ]"
      :style="props.relativeToViewport ? dropdownPosition : {}"
      @click.stop
    >
      <Sketch
        :model-value="currentColorObj"
        @update:model-value="handleColorChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from "vue";
import { Sketch } from "@ckpack/vue-color";
import { variants } from "@/src/constants/customComponentConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  modelValue: { type: String, default: "#164746" },
  pickerSize: { type: Number, default: 24 },
  canType: { type: Boolean, default: false },
  variant: {
    type: String,
    default: variants.VARIANT1,
    validator: (value) => Object.values(variants).includes(value),
  },
  relativeToViewport: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "change", "blur"]);

const isOpen = ref(false);
const hexInput = ref(props.modelValue || "#164746");
const colorPickerRef = ref(null);
const triggerRef = ref(null);
const hasColorChanged = ref(false);
const initialColor = ref(props.modelValue || "#164746");

const currentColorObj = ref({
  hex: props.modelValue || "#164746",
});

const dropdownPosition = computed(() => {
  if (!props.relativeToViewport || !triggerRef.value || !isOpen.value) {
    return {};
  }
  
  const triggerRect = triggerRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const dropdownWidth = 280; // Approximate width of color picker
  const dropdownHeight = 200; // Approximate height of color picker
  
  let left = triggerRect.left;
  let top = triggerRect.top - dropdownHeight - 8;
  
  // Adjust horizontal position if it would go off-screen
  if (left + dropdownWidth > viewportWidth) {
    left = viewportWidth - dropdownWidth - 8;
  }
  if (left < 8) {
    left = 8;
  }
  
  // Adjust vertical position if it would go off-screen
  if (top < 8) {
    top = triggerRect.bottom + 8;
  }
  
  return {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    zIndex: 1000,
  };
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (
      newValue &&
      typeof newValue === "string" &&
      newValue !== hexInput.value
    ) {
      hexInput.value = newValue;
      currentColorObj.value = { hex: newValue };
    }
  },
);

const togglePicker = () => {
  if (!isOpen.value) {
    initialColor.value = props.modelValue || "#164746";
    hasColorChanged.value = false;
  }
  isOpen.value = !isOpen.value;
};

const closePicker = () => {
  isOpen.value = false;
  if (hasColorChanged.value) {
    emit("blur");
    hasColorChanged.value = false;
  }
};

const handleColorChange = (colorData) => {
  let hexValue;

  if (typeof colorData === "string") {
    hexValue = colorData;
  } else if (colorData && typeof colorData === "object") {
    hexValue = colorData.hex || colorData.value || colorData;
  } else {
    return;
  }

  if (typeof hexValue === "string" && hexValue.startsWith("#")) {
    if (hexValue !== initialColor.value) {
      hasColorChanged.value = true;
    }
    hexInput.value = hexValue;
    currentColorObj.value = { hex: hexValue };
    emit("update:modelValue", hexValue);
    emit("change", hexValue);
  }
};

const handleHexInput = (event) => {
  let value = event.target.value;
  if (!value.startsWith("#")) {
    value = "#" + value;
  }

  if (isValidHex(value)) {
    hexInput.value = value;
    currentColorObj.value = { hex: value };
    emit("update:modelValue", value);
    emit("change", value);
  }
};

const handleHexBlur = () => {
  if (!isValidHex(hexInput.value)) {
    hexInput.value = props.modelValue || "#164746";
  }
};

const isValidHex = (hex) => {
  return (
    typeof hex === "string" && /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)
  );
};

const handleClickOutside = (event) => {
  if (colorPickerRef.value && !colorPickerRef.value.contains(event.target)) {
    closePicker();
  }
};

watch(() => isOpen.value, (newValue) => {
  if (newValue && props.relativeToViewport) {
    nextTick(() => {
      // Force position recalculation
      if (triggerRef.value) {
        dropdownPosition.value;
      }
    });
  }
});

onMounted(() => {
  if (props.modelValue && typeof props.modelValue === "string") {
    hexInput.value = props.modelValue;
    currentColorObj.value = { hex: props.modelValue };
  }
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.picker_container {
  padding: 4px 12px 4px 4px;
  align-self: stretch;
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-4);
}
</style>
